import { useContext, useEffect, useState } from "react";
import { Payment, PaymentHeader, PaymentType } from "../../types"; // Replace with actual types
import { Content } from "../../../_myb/layout/components/content";
import { deletePaymentById, getPayment, getPaymentPublicView, getPaymentView, openPaymentPdfInNewTab, openPdfInNewTab, upSertPayment } from "../../api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomPrompt, CustomPromptProps, emptyCustomPromptProps } from "../widgets/components/ModalPrompts";
import PrintDocument from "./PrintTxn";
import { defaultToolbarState, ToolbarContext } from "../../../_myb/layout/components/toolbar/Toolbar";
import ButtonWithLoader from "../widgets/components/ButtonWithLoader";
import { getPaymentConfig, PaymentConfig } from "./TxnConfig";
import { sanitizeRenderedHtml } from "../utils/MiscFunctions";


interface ViewPaymentProps {
    publicView?: boolean;
}

export const ViewPayment: React.FC<ViewPaymentProps> = ({ publicView }) => {
    const navigate = useNavigate();
    const { paymentId } = useParams<{ paymentId: string }>();
    const { paymentType } = useParams<{ paymentType: string }>();
    const [payment, setPayment] = useState<Payment>();
    const [htmlContent, setHtmlContent] = useState('');
    const [isPromptVisible, setPromptVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [customPromptValues, setCustomPromptValues] = useState<CustomPromptProps>(emptyCustomPromptProps);
    const { updateToolbar } = useContext(ToolbarContext);
    const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>()
    const [isModalOpen, setIsModalOpen] = useState(false);
    const updateButtons = () => {
        updateToolbar({
            primaryButton: {
                enabled: true,
                text: `${'Create'.toTranslatedDisplay()} ${paymentConfig?.title?.toTranslatedDisplay() || ''}`,
                shortcutKeys: ['ctrl+g', 'meta+g'],
                shortcutTip: 'Ctrl+g / ⌘+g',
                icon: 'create', // Replace with appropriate icon type
                action: async () => {
                    navigate(`${paymentConfig?.createRoute}`);
                    setIsModalOpen(true);
                }
            },
            secondaryButton: {
                enabled: true,
                text: `Configure Print Template`,
                // className:'btn-light-primary',
                icon: 'create', // Replace with appropriate icon type
                action: async () => {
                    navigate(`/settings/print-templates`);
                    setIsModalOpen(true);
                }
            },
        });
    };

    useEffect(() => {
        updateButtons()
    }, [paymentConfig])

    useEffect(() => {
        const fetchPaymentView = async () => {
            if (paymentId) {
                const apiResult = await getPaymentPublicView(paymentId)
                setHtmlContent(sanitizeRenderedHtml(apiResult.renderPayment))
                setPayment(apiResult.payment)
            }
        };
        fetchPaymentView();

        const config = getPaymentConfig(paymentType as PaymentType || PaymentType.CustomerPayment);
        setPaymentConfig(config);
    }, [paymentId]);

    const handleDownload = async () => {
        setDownloading(true)
        if (payment) await openPaymentPdfInNewTab(payment.paymentHeader.paymentId, payment.paymentHeader.paymentNo || 'Payment');
        setDownloading(false)
    };

    const handlePrint = () => {
        if (htmlContent) {
            const printWindow = window.open('', '', 'height=600,width=800');
            if (printWindow) {
                printWindow.document.write(`
                    <html>
                        <head>
                            <title>{'Print Preview'.toTranslatedDisplay()}</title>
                            <style>
                                @media print {
                                    body {
                                        margin: 0;
                                        padding: 20px;
                                        font-family: Arial, sans-serif;
                                        size: A4;
                                        -webkit-print-color-adjust: exact;
                                    }
                                    @page {
                                        margin: 1cm;
                                        size: portrait;
                                    }
                                    .no-print {
                                        display: none;
                                    }
                                }
                            </style>
                        </head>
                        <body>
                            ${htmlContent}
                        </body>
                    </html>
                `);
                printWindow.document.close();
                printWindow.onload = () => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                };
            } else {
                alert('Unable to open the print window. Please disable pop-up blockers and try again.');
            }
        }
    };

    const handleShare = () => {
        const baseurl = window.location.origin;
        const url = `${baseurl}/payment/${paymentId}`;
        setPromptVisible(true);
        setCustomPromptValues({
            ...customPromptValues,
            secondaryText: 'Close'.toTranslatedDisplay(),
            onConfirm: handleClosePrompt,
            title: 'Share'.toTranslatedDisplay(),
            message: (
                <>
                    <div className="d-flex">
                        <input type="text" className="w-100" value={url} disabled={true} />
                        <div onClick={() => {
                            navigator.clipboard.writeText(url);
                            toast.success('URL copied to clipboard'.toTranslatedDisplay());
                        }} className="cursor-pointer input-group-prepend">
                            <span className="d-inline-block rounded-0 rounded-end input-group-text text-truncate" style={{ height: '100%', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                <i className="bi bi-copy fs-2"></i>
                            </span>
                        </div>
                    </div>
                </>
            ),
        });
    };

    const handleEditPayment = () => {
        navigate(`/transactions/edit-payment/${paymentType}/${paymentId}`);
    };

    const handleClose = () => {
        navigate(`/transactions/view-payments/${paymentType}`);
    };

    const handleSend = () => {
        navigate(`/transactions/send-payment/${paymentType}/${paymentId}`); // Navigate to the edit transaction page
    }

    const handleClosePrompt = () => {
        setPromptVisible(false);
    };

    const confirmDeletePayment = async () => {
        try {
            if (paymentId) {
                const result = await deletePaymentById(paymentId);
                
                toast.success('Payment deleted successfully!'.toTranslatedDisplay());
                navigate(`/transactions/view-payments/${paymentType}`);
                
            } else {
                toast.error('Failed to delete payment.'.toTranslatedDisplay());
            }
        } catch (error) {
            toast.error('Failed to delete payment.'.toTranslatedDisplay());
            console.error('Failed to delete payment:', error);
        }
    };

    const handleDeletePayment = () => {
        setPromptVisible(true);
        setCustomPromptValues({
            ...customPromptValues,
            onConfirm: confirmDeletePayment,
            message: 'Do you really want to delete this payment?'.toTranslatedDisplay(),
            title: 'Confirm Delete'.toTranslatedDisplay(),
            PrimaryText: 'Delete'.toTranslatedDisplay(),
            secondaryText: 'Cancel'.toTranslatedDisplay(),
        });
    };

    const handleCancelPayment = async () => {
        if (payment) {
            const updatedPayment = { ...payment, paymentHeader: { ...payment.paymentHeader } };
            setPayment(updatedPayment);
            const uploadStatus = await upSertPayment(updatedPayment);
            toast.success('Payment cancelled successfully!'.toTranslatedDisplay());
            // navigate(-1);
        } else {
            toast.error('Failed to cancel payment.'.toTranslatedDisplay());
        }
    };

    return (
        <Content>
            <div className="d-flex mb-3 justify-content-center">
                {publicView ? (
                    <>
                        <div className="w-100 d-flex justify-content-center me-20 ">
                            <ButtonWithLoader label={""} isLoading={downloading} onClick={handleDownload}>
                                <i className="bi bi-cash fs-2"></i> Download
                            </ButtonWithLoader>
                        </div>
                    </>
                ) : (
                    <>
                        <ButtonWithLoader label={""} isLoading={downloading} onClick={handleDownload}>{'Download Payment'.toTranslatedDisplay()}</ButtonWithLoader>
                        <button type="button" onClick={handleEditPayment} className="btn btn-secondary mx-2">
                            <i className="bi bi-pencil fs-2"></i>
                        </button>
                        <button type="button" onClick={handlePrint} className="btn btn-secondary me-2">
                            <i className="bi bi-printer fs-2"></i>
                        </button>
                        <button type="button" onClick={handleShare} className="btn btn-secondary me-2">
                            <i className="bi bi-share fs-2"></i>
                        </button>
                        <button type="button" onClick={handleSend} className="btn btn-sm  btn-secondary me-2">
                            <i className="bi bi-send fs-2"></i> {'Send'.toTranslatedDisplay()} {/* Print icon */}
                        </button>
                        <button type="button" onClick={handleDeletePayment} className="btn btn-light-danger me-2">
                            <i className="bi bi-trash fs-2"></i> Delete
                        </button>
                        <button type="button" onClick={handleCancelPayment} className="btn btn-light-danger me-2">
                            <i className="bi bi-slash-circle fs-2"></i> Cancel
                        </button>
                        <button type="button" onClick={handleClose} className="btn btn-light-warning me-2">
                            <i className="bi bi-x-circle fs-2"></i> Close
                        </button>
                    </>
                )}
            </div>

            <CustomPrompt
                isVisible={isPromptVisible}
                onClose={handleClosePrompt}
                onConfirm={customPromptValues.onConfirm}
                message={customPromptValues.message}
                title={customPromptValues.title}
                PrimaryText={customPromptValues.PrimaryText}
                secondaryText={customPromptValues.secondaryText}
            />

            <PrintDocument templateString={htmlContent} />
        </Content>
    );
};
